import { ComponentProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isValidPhoneNumber } from 'react-phone-number-input';

import ActionIconButton from '@components/buttons/ActionIconButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import BasicCard from '@components/cards/BasicCard';
import styles from '@components/explore/Explore.module.css';
import MarketplaceButton from '@components/explore/MarketplaceButton';
import Tag from '@components/labels/Tag';
import Nbsp from '@components/Nbsp';
import PinToggle from '@components/PinToggle';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading2 from '@components/typography/Heading2';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  EmployerBenefitCategoryEnum,
  GetEmployerBenefitsQuery,
  PinResourceTypeEnum
} from '@typing/Generated';
import { isValidHttpUrl, shortenWebsite } from '@utils/stringUtils';

type EmployerBenefit = NonNullable<
  NonNullable<NonNullable<GetEmployerBenefitsQuery['employerBenefits']['edges']>[number]>['node']
>;

type Props = {
  border?: 'error300';
  employerBenefit: EmployerBenefit;
  expanded?: boolean;
  goal?: ComponentProps<typeof PinToggle>['goal'];
  journeyId: string;
  layout: 'horizontal' | 'vertical';
  onRemove?: (employerBenefitId: string) => void;
};

const EmployerBenefitCard = ({
  border,
  employerBenefit,
  expanded = false,
  goal,
  journeyId,
  layout,
  onRemove
}: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    'appEvent'
  );

  const handleClickThrough = (benefitId: string, type: string) => {
    if (myClient) {
      createAppEvent({
        actionKind: AppEventActionKindEnum.employer_benefit_click_through,
        context: type,
        journeyId,
        resourceId: benefitId
      });
    }
  };

  const categories = (
    <BodyCopyBold color="secondary500">
      {employerBenefit.categories.map((category, index) => (
        <span key={category}>
          {category === EmployerBenefitCategoryEnum.OTHER && employerBenefit.categoryOther ? (
            employerBenefit.categoryOther
          ) : (
            <FormattedMessage id={`models.employerBenefitCategory.${category}`} />
          )}
          {index !== employerBenefit.categories.length - 1 && (
            <>
              <FormattedMessage id="dictionary.punctuation.comma" />
              <Nbsp />
            </>
          )}
        </span>
      ))}
    </BodyCopyBold>
  );

  const sanitizedDescription = useSanitizeHtml(employerBenefit.description, journeyId, employerBenefit);

  return (
    <BasicCard border={border} padding="normal">
      <div className={styles.marketplaceItem} data-test="employer-benefit-card">
        <div className={onRemove ? styles.marketplacePinWithRemove : styles.marketplacePinWithoutRemove}>
          <PinToggle
            color="neutral500"
            goal={goal}
            journeyId={journeyId}
            resourceId={employerBenefit.id}
            resourceType={PinResourceTypeEnum.EMPLOYERBENEFIT}
          />
        </div>
        {onRemove && (
          <div className={styles.employerBenefitToggle}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.delete"
              color="neutral500"
              icon="times"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onRemove(employerBenefit.id);
              }}
            />
          </div>
        )}

        {layout === 'vertical' ? (
          <div className={styles.marketplaceBenefitVertical}>
            <div className={styles.center}>
              <Tag
                color="secondary700"
                icon="bookmark"
                key="tag"
                size="large"
                text={intl.formatMessage({ id: 'pages.explore.marketplace.employerSponsored' })}
              />
            </div>
            <div className={styles.textAndImage}>
              <div className={styles.center}>
                {employerBenefit.photoUrl && <img className={styles.marketplaceImage} src={employerBenefit.photoUrl} />}
              </div>
              <div className={styles.marketplaceText}>
                {categories}
                <div className={styles.vendorName}>
                  <Heading2>{employerBenefit.vendorName}</Heading2>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.marketplaceBenefitHorizontal}>
            <div className={styles.marketplaceBenefitHorizontalColumn1}>
              <Tag
                color="secondary700"
                icon="bookmark"
                key="tag"
                size="large"
                text={intl.formatMessage({ id: 'pages.explore.marketplace.employerSponsored' })}
              />
              {employerBenefit.photoUrl && <img className={styles.marketplaceImage} src={employerBenefit.photoUrl} />}
            </div>
            <div>
              <div>{categories}</div>
              <div className={styles.vendorName}>
                <Heading2>{employerBenefit.vendorName}</Heading2>
              </div>
            </div>
          </div>
        )}

        {expanded && (
          <div>
            <div className={styles.benefitContacts}>
              {employerBenefit.emailLabel && employerBenefit.emailLink && (
                <>
                  <div className={styles.benefitContact}>
                    <MarketplaceButton
                      href={
                        isValidHttpUrl(employerBenefit.emailLink)
                          ? employerBenefit.emailLink
                          : `mailto: ${employerBenefit.emailLink}`
                      }
                      icon="at"
                      onClick={() => handleClickThrough(employerBenefit.id, 'email')}
                      text={
                        isValidHttpUrl(employerBenefit.emailLabel)
                          ? shortenWebsite(employerBenefit.emailLabel)
                          : employerBenefit.emailLabel
                      }
                    />
                  </div>
                </>
              )}

              {employerBenefit.phoneNumberLabel && employerBenefit.phoneNumberLink && (
                <div className={styles.benefitContact}>
                  <MarketplaceButton
                    href={
                      isValidPhoneNumber(employerBenefit.phoneNumberLink)
                        ? `tel:${employerBenefit.phoneNumberLink}`
                        : employerBenefit.phoneNumberLink
                    }
                    icon="phone"
                    onClick={() => handleClickThrough(employerBenefit.id, 'phone')}
                    text={employerBenefit.phoneNumberLabel}
                  />
                </div>
              )}
              {employerBenefit.links.length > 0 && (
                <div className={styles.benefitContact}>
                  {employerBenefit.links.map((link, index) => (
                    <div className={index !== 0 ? styles.linkSpacing : ''} key={link}>
                      <MarketplaceButton
                        href={link}
                        icon={index === 0 ? 'globe' : undefined}
                        onClick={() => handleClickThrough(employerBenefit.id, 'website')}
                        text={shortenWebsite(link)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.marketplaceDescription}>
              <div>{sanitizedDescription}</div>
            </div>
            {employerBenefit.links.length > 0 && (
              <div className={styles.marketplaceSignUp}>
                <div>
                  <PrimaryButton
                    href={employerBenefit.links[0]}
                    onClick={e => {
                      e.stopPropagation();
                      handleClickThrough(employerBenefit.id, 'website');
                    }}
                    rel="noopener noreferrer"
                    target="_blank"
                    textKey="pages.explore.marketplace.signUp"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </BasicCard>
  );
};

export default EmployerBenefitCard;
